import { useState, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  useColorMode,
  Icon,
  Badge,
  Button,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  Input,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor } from "../brandConfig";
import {
  FaServer,
  FaUsers,
  FaBalanceScale,
  FaCoins,
  FaChartLine,
  FaArrowRight,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSun,
  FaCalendarAlt,
  FaCalendarCheck,
  FaPercentage,
  FaDollarSign,
} from "react-icons/fa";
import { GiTwoCoins } from "react-icons/gi";

// Network parameters
const TOTAL_VALIDATORS = 52;
const TOTAL_SUPPLY = 52_000_000;
const BASE_FEE_POOL_DAILY = 10000; // Example: $10k daily fees from games
const DEVELOPMENT_SHARE = 0.20; // Fixed 20%
const TREASURY_SHARE = 0.10; // Fixed 10%

// Calculate pendulum position based on network health
const calculatePendulum = (
  activeValidators: number,
  totalStaked: number,
  stakeConcentration: number // Top 3 validators % of total stake
) => {
  // Base shares
  let validatorShare = 0.40;
  let delegatorShare = 0.30;

  // Swing based on validator count
  if (activeValidators < 40) {
    validatorShare = 0.50;
    delegatorShare = 0.20;
  } else if (activeValidators < 52) {
    validatorShare = 0.45;
    delegatorShare = 0.25;
  }

  // Swing based on total staked
  const stakedPercent = (totalStaked / TOTAL_SUPPLY) * 100;
  if (stakedPercent < 25) {
    // Need more staking - swing to delegators
    validatorShare = Math.max(0.30, validatorShare - 0.10);
    delegatorShare = Math.min(0.40, delegatorShare + 0.10);
  } else if (stakedPercent > 50) {
    // Plenty staked - balanced
    validatorShare = 0.40;
    delegatorShare = 0.30;
  }

  // Swing based on concentration
  if (stakeConcentration > 50) {
    // Too concentrated - swing to delegators to encourage distribution
    validatorShare = Math.max(0.30, validatorShare - 0.05);
    delegatorShare = Math.min(0.40, delegatorShare + 0.05);
  }

  return {
    validatorShare,
    delegatorShare,
    developmentShare: DEVELOPMENT_SHARE,
    treasuryShare: TREASURY_SHARE,
  };
};

export const ValidatorDelegatorPage = () => {
  const { colorMode } = useColorMode();

  // Network state (these would come from chain in production)
  const [activeValidators, setActiveValidators] = useState(45);
  const [totalStaked, setTotalStaked] = useState(15_000_000); // 15M tokens staked
  const [stakeConcentration, setStakeConcentration] = useState(35); // Top 3 have 35%

  // User inputs
  const [userTokens, setUserTokens] = useState("10000");
  const [dailyFeePool, setDailyFeePool] = useState(BASE_FEE_POOL_DAILY);

  // Calculate current pendulum position
  const pendulum = useMemo(
    () => calculatePendulum(activeValidators, totalStaked, stakeConcentration),
    [activeValidators, totalStaked, stakeConcentration]
  );

  // Calculate rewards
  const userTokensNum = parseFloat(userTokens) || 0;
  const stakedPercent = (totalStaked / TOTAL_SUPPLY) * 100;

  // Validator rewards (if user runs a validator)
  const validatorPoolDaily = dailyFeePool * pendulum.validatorShare;
  const validatorRewardDaily = validatorPoolDaily / activeValidators;
  const validatorRewardMonthly = validatorRewardDaily * 30;
  const validatorRewardYearly = validatorRewardDaily * 365;

  // Delegator rewards (proportional to stake)
  const delegatorPoolDaily = dailyFeePool * pendulum.delegatorShare;
  const userStakeShare = totalStaked > 0 ? userTokensNum / totalStaked : 0;
  const delegatorRewardDaily = delegatorPoolDaily * userStakeShare;
  const delegatorRewardMonthly = delegatorRewardDaily * 30;
  const delegatorRewardYearly = delegatorRewardDaily * 365;

  // APY calculations (assuming 1 token = $1 for simplicity)
  const validatorAPY = userTokensNum > 0 ? (validatorRewardYearly / userTokensNum) * 100 : 0;
  const delegatorAPY = userTokensNum > 0 ? (delegatorRewardYearly / userTokensNum) * 100 : 0;

  // Pendulum visual position (0 = validators, 100 = delegators, 50 = balanced)
  const pendulumVisual = ((pendulum.delegatorShare - 0.20) / 0.20) * 50 + 25;

  return (
    <>
      <NavbarWithCallToAction />
      <Box
        minH="100vh"
        bg="#0a0a0f"
        color={getColor("text.primary", colorMode)}
      >
        <Container maxW="1000px" py={20}>
          <VStack spacing={10} align="stretch">
            {/* Header */}
            <VStack spacing={4} textAlign="center">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #f59e0b, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                INCENTIVE PENDULUM
              </Badge>
              <Heading
                size="2xl"
                color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}
              >
                Validator vs Delegator Calculator
              </Heading>
              <Text
                fontSize="xl"
                color={getColor("text.secondary", colorMode)}
                maxW="700px"
              >
                Compare earnings as a validator or delegator. The pendulum automatically adjusts
                rewards based on network health - see how it affects your potential earnings.
              </Text>
            </VStack>

            {/* Current Network Status */}
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#3b82f6"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">Validator Slots</StatLabel>
                  <StatNumber color="#3b82f6" fontSize="xl">
                    {activeValidators} / {TOTAL_VALIDATORS}
                  </StatNumber>
                  <StatHelpText color={activeValidators < 52 ? "#10b981" : getColor("text.secondary", colorMode)} fontSize="xs">
                    {activeValidators < 52 ? `${52 - activeValidators} slots open!` : "All slots filled"}
                  </StatHelpText>
                </Stat>
              </Box>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#64ffda"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">Total Staked</StatLabel>
                  <StatNumber color="#64ffda" fontSize="xl">
                    {(totalStaked / 1_000_000).toFixed(1)}M
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)} fontSize="xs">
                    {stakedPercent.toFixed(1)}% of supply
                  </StatHelpText>
                </Stat>
              </Box>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(139, 92, 246, 0.1)" : "purple.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#8b5cf6"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">Validator Share</StatLabel>
                  <StatNumber color="#8b5cf6" fontSize="xl">
                    {(pendulum.validatorShare * 100).toFixed(0)}%
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)} fontSize="xs">of fee pool</StatHelpText>
                </Stat>
              </Box>
              <Box
                p={4}
                bg={colorMode === "dark" ? "rgba(245, 158, 11, 0.1)" : "orange.50"}
                borderRadius="lg"
                border="1px solid"
                borderColor="#f59e0b"
                textAlign="center"
              >
                <Stat size="sm">
                  <StatLabel color={getColor("text.secondary", colorMode)} fontSize="xs">Delegator Share</StatLabel>
                  <StatNumber color="#f59e0b" fontSize="xl">
                    {(pendulum.delegatorShare * 100).toFixed(0)}%
                  </StatNumber>
                  <StatHelpText color={getColor("text.secondary", colorMode)} fontSize="xs">of fee pool</StatHelpText>
                </Stat>
              </Box>
            </SimpleGrid>

            {/* How It Works Explanation */}
            <Alert
              status="info"
              variant="left-accent"
              borderRadius="lg"
              bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.50"}
              borderColor="#3b82f6"
            >
              <AlertIcon color="#3b82f6" />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                  How the 52 Validator System Works
                </Text>
                <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                  <strong>Maximum 52 validators</strong> - one for each card in the deck. Anyone with 1,000 RIO (alpha) / 52,000 B52 + a server can claim an open slot.
                  When slots are available, the pendulum swings toward validators (higher rewards) to fill them.
                  Once all 52 slots are filled, rewards balance between validators and delegators.
                </Text>
                <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                  <strong>Can't run a validator?</strong> Delegate your tokens to earn passive income without any hardware!
                </Text>
              </VStack>
            </Alert>

            {/* Pendulum Visualization */}
            <Box
              p={6}
              borderRadius="xl"
              bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
              border="2px solid"
              borderColor="#f59e0b"
              boxShadow="0 0 20px rgba(245, 158, 11, 0.1)"
            >
              <VStack spacing={6} align="stretch">
                <HStack justify="center">
                  <Icon as={FaBalanceScale} color="#f59e0b" boxSize={6} />
                  <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                    The Incentive Pendulum
                  </Heading>
                </HStack>

                <Text textAlign="center" color={getColor("text.secondary", colorMode)} fontSize="sm">
                  Drag the sliders to simulate network conditions. When validator slots are open, rewards swing toward validators to fill them.
                  When staking is low, rewards swing toward delegators to encourage more staking.
                </Text>

                {/* Visual Pendulum */}
                <Box
                  h="120px"
                  position="relative"
                  bg={colorMode === "dark" ? "gray.800" : "gray.100"}
                  borderRadius="lg"
                  overflow="hidden"
                >
                  {/* Background gradient */}
                  <Box
                    position="absolute"
                    inset={0}
                    bgGradient="linear(to-r, rgba(59, 130, 246, 0.3), transparent, rgba(100, 255, 218, 0.3))"
                  />

                  {/* Labels */}
                  <HStack
                    position="absolute"
                    top={2}
                    left={0}
                    right={0}
                    justify="space-between"
                    px={4}
                  >
                    <VStack spacing={0}>
                      <Icon as={FaServer} color="#3b82f6" />
                      <Text fontSize="xs" fontWeight="bold" color="#3b82f6">VALIDATORS</Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>
                        {(pendulum.validatorShare * 100).toFixed(0)}%
                      </Text>
                    </VStack>
                    <VStack spacing={0}>
                      <Icon as={FaUsers} color="#64ffda" />
                      <Text fontSize="xs" fontWeight="bold" color="#64ffda">DELEGATORS</Text>
                      <Text fontSize="xs" color={getColor("text.secondary", colorMode)}>
                        {(pendulum.delegatorShare * 100).toFixed(0)}%
                      </Text>
                    </VStack>
                  </HStack>

                  {/* Pendulum arm */}
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    w="4px"
                    h="50px"
                    bg="#f59e0b"
                    borderRadius="full"
                    transformOrigin="top center"
                    transform={`translateX(-50%) rotate(${(pendulumVisual - 50) * 0.9}deg)`}
                    transition="transform 0.5s ease-out"
                  />

                  {/* Pendulum pivot */}
                  <Box
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    w="16px"
                    h="16px"
                    borderRadius="full"
                    bg="#f59e0b"
                    boxShadow="0 0 10px #f59e0b"
                  />

                  {/* Pendulum bob */}
                  <Box
                    position="absolute"
                    top="calc(50% + 45px)"
                    left={`${pendulumVisual}%`}
                    transform="translateX(-50%)"
                    w="24px"
                    h="24px"
                    borderRadius="full"
                    bg="#f59e0b"
                    boxShadow="0 0 15px #f59e0b"
                    transition="left 0.5s ease-out"
                  />
                </Box>

                {/* Network Sliders */}
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  {/* Validators Slider */}
                  <Box>
                    <Text fontSize="sm" mb={2} color={getColor("text.secondary", colorMode)}>
                      Validator Slots Filled: <strong>{activeValidators}/52</strong>
                      {activeValidators < 52 && (
                        <Text as="span" color="#10b981" ml={2}>({52 - activeValidators} open)</Text>
                      )}
                    </Text>
                    <Slider
                      value={activeValidators}
                      min={1}
                      max={52}
                      onChange={(v) => setActiveValidators(v)}
                    >
                      <SliderTrack bg="gray.300">
                        <SliderFilledTrack bg="#3b82f6" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="#3b82f6" />
                    </Slider>
                  </Box>

                  {/* Staked Slider */}
                  <Box>
                    <Text fontSize="sm" mb={2} color={getColor("text.secondary", colorMode)}>
                      Total Staked: <strong>{(totalStaked / 1_000_000).toFixed(1)}M</strong> ({stakedPercent.toFixed(0)}%)
                    </Text>
                    <Slider
                      value={totalStaked}
                      min={1_000_000}
                      max={40_000_000}
                      step={1_000_000}
                      onChange={(v) => setTotalStaked(v)}
                    >
                      <SliderTrack bg="gray.300">
                        <SliderFilledTrack bg="#64ffda" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="#64ffda" />
                    </Slider>
                  </Box>

                  {/* Concentration Slider */}
                  <Box>
                    <Text fontSize="sm" mb={2} color={getColor("text.secondary", colorMode)}>
                      Top 3 Concentration: <strong>{stakeConcentration}%</strong>
                    </Text>
                    <Slider
                      value={stakeConcentration}
                      min={10}
                      max={70}
                      onChange={(v) => setStakeConcentration(v)}
                    >
                      <SliderTrack bg="gray.300">
                        <SliderFilledTrack bg="#8b5cf6" />
                      </SliderTrack>
                      <SliderThumb boxSize={5} bg="#8b5cf6" />
                    </Slider>
                  </Box>
                </SimpleGrid>
              </VStack>
            </Box>

            {/* Earnings Calculator */}
            <Box
              p={6}
              borderRadius="xl"
              bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
              border="1px solid"
              borderColor={getColor("border.medium", colorMode)}
            >
              <VStack spacing={6} align="stretch">
                <Heading size="md" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                  Your Earnings Calculator
                </Heading>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" mb={2} color={getColor("text.secondary", colorMode)}>
                      Your Token Amount (RIO/B52)
                    </Text>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Icon as={GiTwoCoins} color="#64ffda" />
                      </InputLeftElement>
                      <Input
                        type="number"
                        value={userTokens}
                        onChange={(e) => setUserTokens(e.target.value)}
                        placeholder="10000"
                        borderColor={getColor("border.medium", colorMode)}
                        _focus={{ borderColor: "#64ffda", boxShadow: "0 0 0 1px #64ffda" }}
                      />
                    </InputGroup>
                  </Box>
                  <Box>
                    <Text fontSize="sm" mb={2} color={getColor("text.secondary", colorMode)}>
                      Daily Network Fee Pool ($)
                    </Text>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <Text color="#10b981" fontWeight="bold">$</Text>
                      </InputLeftElement>
                      <Input
                        type="number"
                        value={dailyFeePool}
                        onChange={(e) => setDailyFeePool(parseFloat(e.target.value) || 0)}
                        placeholder="10000"
                        borderColor={getColor("border.medium", colorMode)}
                        _focus={{ borderColor: "#10b981", boxShadow: "0 0 0 1px #10b981" }}
                      />
                    </InputGroup>
                  </Box>
                </SimpleGrid>

                {/* Comparison Cards */}
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  {/* Validator Earnings */}
                  <Box
                    p={5}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.15)" : "blue.50"}
                    border="2px solid"
                    borderColor="#3b82f6"
                  >
                    <VStack spacing={4} align="stretch">
                      <HStack>
                        <Box
                          p={2}
                          borderRadius="lg"
                          bg="rgba(59, 130, 246, 0.2)"
                        >
                          <Icon as={FaServer} color="#3b82f6" boxSize={6} />
                        </Box>
                        <Text fontWeight="bold" fontSize="lg" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                          As a Validator
                        </Text>
                      </HStack>

                      <Divider borderColor="blue.300" />

                      {/* Earnings with Icons */}
                      <VStack spacing={3} align="stretch">
                        <HStack justify="space-between" p={2} borderRadius="md" bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.100"}>
                          <HStack>
                            <Icon as={FaSun} color="#f59e0b" boxSize={4} />
                            <Text color={getColor("text.secondary", colorMode)} fontSize="sm">Daily</Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaDollarSign} color="#10b981" boxSize={4} />
                            <Text fontWeight="bold" color="#3b82f6" fontSize="lg">
                              {validatorRewardDaily.toFixed(2)}
                            </Text>
                          </HStack>
                        </HStack>

                        <HStack justify="space-between" p={2} borderRadius="md" bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.100"}>
                          <HStack>
                            <Icon as={FaCalendarAlt} color="#8b5cf6" boxSize={4} />
                            <Text color={getColor("text.secondary", colorMode)} fontSize="sm">Monthly</Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaDollarSign} color="#10b981" boxSize={4} />
                            <Text fontWeight="bold" color="#3b82f6" fontSize="lg">
                              {validatorRewardMonthly.toFixed(2)}
                            </Text>
                          </HStack>
                        </HStack>

                        <HStack justify="space-between" p={2} borderRadius="md" bg={colorMode === "dark" ? "rgba(59, 130, 246, 0.1)" : "blue.100"}>
                          <HStack>
                            <Icon as={FaCalendarCheck} color="#10b981" boxSize={4} />
                            <Text color={getColor("text.secondary", colorMode)} fontSize="sm">Yearly</Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaDollarSign} color="#10b981" boxSize={4} />
                            <Text fontWeight="bold" color="#3b82f6" fontSize="lg">
                              {validatorRewardYearly.toFixed(2)}
                            </Text>
                          </HStack>
                        </HStack>
                      </VStack>

                      <Divider borderColor="blue.300" />

                      {/* APY with Icon */}
                      <HStack justify="space-between" p={3} borderRadius="lg" bg="rgba(59, 130, 246, 0.2)">
                        <HStack>
                          <Icon as={FaPercentage} color="#3b82f6" boxSize={5} />
                          <Text color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"} fontWeight="medium">APY</Text>
                        </HStack>
                        <Badge
                          colorScheme="blue"
                          fontSize="xl"
                          px={4}
                          py={1}
                          borderRadius="full"
                        >
                          {validatorAPY.toFixed(1)}%
                        </Badge>
                      </HStack>

                      <Alert status="info" borderRadius="md" fontSize="xs">
                        <AlertIcon boxSize={4} />
                        <Text>Requires <strong>1,000 RIO</strong> (alpha) / <strong>52,000 B52</strong> + server</Text>
                      </Alert>
                    </VStack>
                  </Box>

                  {/* Delegator Earnings */}
                  <Box
                    p={5}
                    borderRadius="lg"
                    bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.15)" : "teal.50"}
                    border="2px solid"
                    borderColor="#64ffda"
                  >
                    <VStack spacing={4} align="stretch">
                      <HStack>
                        <Box
                          p={2}
                          borderRadius="lg"
                          bg="rgba(100, 255, 218, 0.2)"
                        >
                          <Icon as={FaUsers} color="#64ffda" boxSize={6} />
                        </Box>
                        <Text fontWeight="bold" fontSize="lg" color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                          As a Delegator
                        </Text>
                      </HStack>

                      <Divider borderColor="teal.300" />

                      {/* Earnings with Icons */}
                      <VStack spacing={3} align="stretch">
                        <HStack justify="space-between" p={2} borderRadius="md" bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.100"}>
                          <HStack>
                            <Icon as={FaSun} color="#f59e0b" boxSize={4} />
                            <Text color={getColor("text.secondary", colorMode)} fontSize="sm">Daily</Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaDollarSign} color="#10b981" boxSize={4} />
                            <Text fontWeight="bold" color="#64ffda" fontSize="lg">
                              {delegatorRewardDaily.toFixed(2)}
                            </Text>
                          </HStack>
                        </HStack>

                        <HStack justify="space-between" p={2} borderRadius="md" bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.100"}>
                          <HStack>
                            <Icon as={FaCalendarAlt} color="#8b5cf6" boxSize={4} />
                            <Text color={getColor("text.secondary", colorMode)} fontSize="sm">Monthly</Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaDollarSign} color="#10b981" boxSize={4} />
                            <Text fontWeight="bold" color="#64ffda" fontSize="lg">
                              {delegatorRewardMonthly.toFixed(2)}
                            </Text>
                          </HStack>
                        </HStack>

                        <HStack justify="space-between" p={2} borderRadius="md" bg={colorMode === "dark" ? "rgba(100, 255, 218, 0.1)" : "teal.100"}>
                          <HStack>
                            <Icon as={FaCalendarCheck} color="#10b981" boxSize={4} />
                            <Text color={getColor("text.secondary", colorMode)} fontSize="sm">Yearly</Text>
                          </HStack>
                          <HStack>
                            <Icon as={FaDollarSign} color="#10b981" boxSize={4} />
                            <Text fontWeight="bold" color="#64ffda" fontSize="lg">
                              {delegatorRewardYearly.toFixed(2)}
                            </Text>
                          </HStack>
                        </HStack>
                      </VStack>

                      <Divider borderColor="teal.300" />

                      {/* APY with Icon */}
                      <HStack justify="space-between" p={3} borderRadius="lg" bg="rgba(100, 255, 218, 0.2)">
                        <HStack>
                          <Icon as={FaPercentage} color="#64ffda" boxSize={5} />
                          <Text color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"} fontWeight="medium">APY</Text>
                        </HStack>
                        <Badge
                          colorScheme="teal"
                          fontSize="xl"
                          px={4}
                          py={1}
                          borderRadius="full"
                        >
                          {delegatorAPY.toFixed(1)}%
                        </Badge>
                      </HStack>

                      <Alert status="success" borderRadius="md" fontSize="xs">
                        <AlertIcon boxSize={4} />
                        <Text><strong>No hardware needed</strong> - just stake with a validator</Text>
                      </Alert>
                    </VStack>
                  </Box>
                </SimpleGrid>

                {/* Recommendation */}
                <Box
                  p={4}
                  borderRadius="lg"
                  bgGradient={validatorAPY > delegatorAPY
                    ? "linear(to-r, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1))"
                    : "linear(to-r, rgba(100, 255, 218, 0.2), rgba(100, 255, 218, 0.1))"
                  }
                  border="2px solid"
                  borderColor={validatorAPY > delegatorAPY ? "#3b82f6" : "#64ffda"}
                >
                  <HStack spacing={3}>
                    <Icon
                      as={FaCheckCircle}
                      color={validatorAPY > delegatorAPY ? "#3b82f6" : "#64ffda"}
                      boxSize={5}
                    />
                    <Text color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                      <strong>Recommendation:</strong> With current network conditions,
                      {validatorAPY > delegatorAPY
                        ? ` being a Validator earns ${(validatorAPY - delegatorAPY).toFixed(1)}% more APY. Consider running a validator if you have the hardware.`
                        : ` being a Delegator is more accessible. The pendulum is swinging to incentivize more staking.`
                      }
                    </Text>
                  </HStack>
                </Box>
              </VStack>
            </Box>

            {/* Fee Distribution Table */}
            <Box
              p={6}
              borderRadius="xl"
              bg={colorMode === "dark" ? "rgba(31, 41, 55, 0.5)" : "white"}
              border="1px solid"
              borderColor={getColor("border.medium", colorMode)}
            >
              <Heading size="md" mb={4} color={colorMode === "light" ? getColor("text.primary", colorMode) : "white"}>
                Current Fee Distribution
              </Heading>

              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color={getColor("text.secondary", colorMode)}>Recipient</Th>
                      <Th color={getColor("text.secondary", colorMode)} isNumeric>Share</Th>
                      <Th color={getColor("text.secondary", colorMode)} isNumeric>Daily ($)</Th>
                      <Th color={getColor("text.secondary", colorMode)}>Notes</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    <Tr>
                      <Td color="#3b82f6" fontWeight="bold">Validators</Td>
                      <Td isNumeric color="#3b82f6">{(pendulum.validatorShare * 100).toFixed(0)}%</Td>
                      <Td isNumeric>${(dailyFeePool * pendulum.validatorShare).toLocaleString()}</Td>
                      <Td color={getColor("text.secondary", colorMode)} fontSize="xs">Shared among {activeValidators} validators</Td>
                    </Tr>
                    <Tr>
                      <Td color="#64ffda" fontWeight="bold">Delegators</Td>
                      <Td isNumeric color="#64ffda">{(pendulum.delegatorShare * 100).toFixed(0)}%</Td>
                      <Td isNumeric>${(dailyFeePool * pendulum.delegatorShare).toLocaleString()}</Td>
                      <Td color={getColor("text.secondary", colorMode)} fontSize="xs">Proportional to stake</Td>
                    </Tr>
                    <Tr>
                      <Td color="#8b5cf6" fontWeight="bold">Development</Td>
                      <Td isNumeric color="#8b5cf6">{(pendulum.developmentShare * 100).toFixed(0)}%</Td>
                      <Td isNumeric>${(dailyFeePool * pendulum.developmentShare).toLocaleString()}</Td>
                      <Td color={getColor("text.secondary", colorMode)} fontSize="xs">Fixed - protocol development</Td>
                    </Tr>
                    <Tr>
                      <Td color="#f59e0b" fontWeight="bold">Treasury</Td>
                      <Td isNumeric color="#f59e0b">{(pendulum.treasuryShare * 100).toFixed(0)}%</Td>
                      <Td isNumeric>${(dailyFeePool * pendulum.treasuryShare).toLocaleString()}</Td>
                      <Td color={getColor("text.secondary", colorMode)} fontSize="xs">Fixed - emergency reserves</Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>

            {/* Action Buttons */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Button
                as={RouterLink}
                to="/token-sale"
                size="lg"
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, #64ffda, #3b82f6)",
                  transform: "translateY(-2px)",
                }}
                leftIcon={<FaCoins />}
              >
                Buy Tokens Now
              </Button>
              <Button
                as={RouterLink}
                to="/tokenomics"
                variant="outline"
                size="lg"
                borderColor="#8b5cf6"
                color="#8b5cf6"
                _hover={{ bg: "rgba(139, 92, 246, 0.1)" }}
                leftIcon={<FaChartLine />}
              >
                Full Tokenomics
              </Button>
              <Button
                as={RouterLink}
                to="/use/stake-b52"
                variant="outline"
                size="lg"
                borderColor="#64ffda"
                color="#64ffda"
                _hover={{ bg: "rgba(100, 255, 218, 0.1)" }}
                leftIcon={<FaArrowRight />}
              >
                Start Staking
              </Button>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
    </>
  );
};

export default ValidatorDelegatorPage;
